import React, { useEffect, useMemo, useState } from "react"

import moment from "moment"
import * as PapaParse from "papaparse"
import { useSearchParams } from "react-router-dom"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"

import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"

import { GENERAL_CONFIG } from "../../../config/general.config"
import { UserExportColumn } from "../../../config/organization.config"
import { CsvHelper } from "../../../helpers/csv.helper"
import { PermissionsHelper } from "../../../helpers/permissions.helper"
import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { organizationSelector } from "../../../store/selectors/organization.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import {
    organizationInvitesGetList,
    organizationUsersGetList,
} from "../../../store/thunks/organization.thunks"
import { OrganizationUserInvitePopupComponent } from "../../popups/organizationUserInvitePopup.component"
import { OrganizationInvitesListComponent } from "./organizationInvitesList.component"
import { OrganizationUsersListComponent } from "./organizationUsersList.component"

import exportIcon from "@front-shared/assets/images/icons/download-up-dark.svg"
import plusIcon from "@front-shared/assets/images/icons/plus-dark.svg"

export const OrganizationUsersComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const [searchParams, setSearchParams] = useSearchParams()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const organizationState = useAppSelector(organizationSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const [isInitiated, setIsInitiated] = useState<boolean>(false)
    const [isUserInviteActive, setIsUserInviteActive] = useState<boolean>(false)

    const canManageUsers = useMemo<boolean>(
        () =>
            PermissionsHelper.hasPermission(
                OneVerifyPermission.managePracticeUsers,
                selectedPracticeRole,
            ),
        [selectedPracticeRole],
    )

    const isListLoading = useMemo<boolean>(() => {
        return (
            RuntimeHelper.isOrganizationUsersListLoading() ||
            RuntimeHelper.isOrganizationUserInvitesListLoading()
        )
    }, [runtimeState.isLoading])

    // On mount if create was requested activate creation popup
    useEffect(() => {
        const isInvite = !!searchParams.get(
            GENERAL_CONFIG.urlSearchParamsKeys.create,
        )

        if (!isInvite) {
            return
        }

        setIsUserInviteActive(true)

        // clean url
        searchParams.delete(GENERAL_CONFIG.urlSearchParamsKeys.create)
        setSearchParams(searchParams)
    }, [])

    // Reload users and invites list on practice change
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            organizationUsersGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )

        dispatch(
            organizationInvitesGetList({
                payload: undefined,
                practice: selectedPracticeRole.practice,
            }),
        )

        // Mark component as initiated
        // Without this spinner jumps and messy UI happens
        setTimeout(() => {
            setIsInitiated(true)
        })
    }, [selectedPracticeRole?.id])

    const onExportCsv = () => {
        if (!organizationState.users.length) {
            return
        }

        const headers = Object.values(UserExportColumn)
        const rows: Record<UserExportColumn, string>[] =
            organizationState.users.map(user => ({
                [UserExportColumn.FirstName]: user.firstName || "",
                [UserExportColumn.LastName]: user.lastName || "",
                [UserExportColumn.Email]: user.email || "",
                [UserExportColumn.Role]: user.role?.label || "",
                [UserExportColumn.MFAEnabled]: user.isMFAEnabled ? "Yes" : "No",
                [UserExportColumn.CreditsUsed]: (
                    user.coverageChecksThisMonthCount || 0
                ).toString(),
                [UserExportColumn.CreatedAt]: moment(user.createdAt).format(
                    GENERAL_CONFIG.defaultMomentDateTimeFormat,
                ),
            }))

        const csvData: string[][] = [
            headers,
            ...rows.map(row => Object.values(row)),
        ]

        CsvHelper.createAndDownloadCsvDocument(
            PapaParse.unparse(csvData),
            `${selectedPracticeRole?.practice.name}-UsageReport`,
        )
    }

    return canManageUsers && selectedPracticeRole ? (
        <div
            className={`
                ${props.className}
                max-w-[840px] mx-auto relative pb-[24px]
            `}
        >
            <PageHelmetElement
                title="Users"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <PrimaryText className="mr-[16px]" typography="h4">
                        Users
                    </PrimaryText>

                    <ButtonElement
                        label="Add user"
                        icon={plusIcon}
                        htmlType="button"
                        type="default"
                        onClick={() => {
                            setIsUserInviteActive(true)
                        }}
                    />
                </div>

                <ButtonElement
                    label="Export CSV"
                    icon={exportIcon}
                    htmlType="button"
                    type="default"
                    onClick={onExportCsv}
                    disabled={!organizationState.users.length}
                />
            </div>

            {isListLoading || !isInitiated ? (
                <SpinnerElement containerClassName="mt-[36px] text-center" />
            ) : organizationState.users.length < 2 &&
              !organizationState.invites.length ? (
                <div className="mt-[16px] bg-brand-warmLight05 rounded-[16px] px-[24px] py-[40px]">
                    <PrimaryText>
                        Manage access to the organization, individuals' roles,
                        and track how many checks each person has run.
                    </PrimaryText>
                </div>
            ) : (
                <>
                    <OrganizationUsersListComponent className="mt-[16px]" />

                    <OrganizationInvitesListComponent />
                </>
            )}

            <OrganizationUserInvitePopupComponent
                isActive={isUserInviteActive}
                onDone={() => {
                    setIsUserInviteActive(false)
                }}
            />
        </div>
    ) : null
}
